import React, { useState, useRef, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import QuoteInput from './components/QuoteInput';
import PosterPreview from './components/PosterPreview';
import CustomizationPanel from './components/CustomizationPanel';
import InspirationFeed from './components/InspirationFeed';
import AmazonStorefront from './components/AmazonStorefront';
import Header from './components/Header';
import RemoteHeader from './components/RemoteHeader';
import RemoteFooter from './components/RemoteFooter';
import AboutUs from './pages/AboutUs';
import ContactUs from './pages/ContactUs';
import PrivacyPolicy from './pages/PrivacyPolicy';
import BlogList from './pages/BlogList';
import BlogPost from './pages/BlogPost';
import { predefinedStyles } from './data/styles';
import supabase from './supabase/supabase';
import './App.css';

function Home({ 
  quote, setQuote, 
  author, setAuthor, 
  customizations, setCustomizations, 
  currentStep, setCurrentStep, 
  unlockedResolution, setUnlockedResolution, 
  autoDownloadTrigger, setAutoDownloadTrigger, 
  paymentStatus, setPaymentStatus, 
  posterRef, resetApp 
}) {
  return (
    <div className="pt-[56px]">
      <Header onReset={resetApp} currentStep={currentStep} onStepChange={setCurrentStep} />
      <main className="flex-grow container mx-auto px-4 py-8 max-w-6xl">
        <AnimatePresence mode="wait">
          {currentStep === 'input' && (
            <motion.div key="input" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <QuoteInput 
                onSubmit={(q, a) => {
                  setQuote(q);
                  setAuthor(a);
                  setCurrentStep('customize');
                }} 
                initialQuote={quote} 
                initialAuthor={author} 
                onQuoteChange={setQuote} 
                onAuthorChange={setAuthor} 
              />
              <InspirationFeed onSelectQuote={(q, a) => {
                setQuote(q);
                setAuthor(a);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }} />
              <AmazonStorefront title="Tools for Creators" />
            </motion.div>
          )}

          {(currentStep === 'customize' || currentStep === 'preview') && (
            <motion.div key="customize" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col gap-12">
              <div className="grid lg:grid-cols-2 gap-8">
                <CustomizationPanel 
                  quote={quote}
                  author={author}
                  onQuoteChange={setQuote}
                  onAuthorChange={setAuthor}
                  customizations={customizations} 
                  onCustomizationChange={(k, v) => setCustomizations(p => ({ ...p, [k]: v }))} 
                  onBack={() => setCurrentStep('input')} 
                  onPreview={() => setCurrentStep('preview')} 
                />
                <PosterPreview 
                  ref={posterRef} 
                  quote={quote} 
                  author={author} 
                  customizations={customizations} 
                  unlockedResolution={unlockedResolution} 
                  autoDownloadTrigger={autoDownloadTrigger} 
                  onAutoDownloadComplete={() => setAutoDownloadTrigger(false)} 
                  onCloseUnlock={() => {
                    setUnlockedResolution(null);
                    setPaymentStatus('idle');
                  }} 
                />
              </div>
              <AmazonStorefront title="Premium Display Gear" layout="grid" />
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}

function App() {
  const [quote, setQuote] = useState('');
  const [author, setAuthor] = useState('');
  const [customizations, setCustomizations] = useState({
    font: predefinedStyles[0].font,
    background: predefinedStyles[0].background,
    textColor: predefinedStyles[0].textColor,
    textAlign: predefinedStyles[0].textAlign,
    fontSize: predefinedStyles[0].fontSize,
    orientation: 'portrait',
    typographyType: 'dynamic',
    accentColor: predefinedStyles[0].accentColor || '#6366f1',
    showDecorations: true,
    decorationStyle: 'auto',
    textTransform: 'layout'
  });
  const [currentStep, setCurrentStep] = useState('input');
  const [unlockedResolution, setUnlockedResolution] = useState(null);
  const [autoDownloadTrigger, setAutoDownloadTrigger] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('idle');
  const posterRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const status = params.get('status');
    const res = params.get('res');

    if (status === 'success' && sessionId) {
      setPaymentStatus('verifying');
      const savedData = localStorage.getItem('pending_poster_design');
      if (savedData) {
        try {
          const parsed = JSON.parse(savedData);
          setQuote(parsed.quote);
          setAuthor(parsed.author);
          setCustomizations(parsed.customizations);
          setCurrentStep('preview');
          verifyPayment(sessionId, res || parsed.resolution, 0);
        } catch (e) {
          console.error("Data restoration failed", e);
        }
      }
    }
  }, []);

  const verifyPayment = async (sessionId, res, attempts) => {
    if (!sessionId) return;
    try {
      const { data } = await supabase
        .from('premium_downloads_1740000000000')
        .select('status,resolution')
        .eq('stripe_session_id', sessionId)
        .maybeSingle();

      if (data && data.status === 'completed') {
        setUnlockedResolution(res || data.resolution);
        setPaymentStatus('success');
        window.history.replaceState({}, document.title, window.location.pathname);
        setTimeout(() => {
          setAutoDownloadTrigger(true);
          localStorage.removeItem('pending_poster_design');
        }, 1000);
      } else if (attempts < 100) {
        setTimeout(() => verifyPayment(sessionId, res, attempts + 1), 3000);
      }
    } catch (err) {
      setTimeout(() => verifyPayment(sessionId, res, attempts + 1), 5000);
    }
  };

  const resetApp = () => {
    setQuote('');
    setAuthor('');
    setCurrentStep('input');
    setUnlockedResolution(null);
    setAutoDownloadTrigger(false);
    setPaymentStatus('idle');
    localStorage.removeItem('pending_poster_design');
  };

  return (
    <Router>
      <div className="min-h-screen flex flex-col bg-slate-50">
        <RemoteHeader />
        <Routes>
          <Route path="/" element={<Home 
            quote={quote} setQuote={setQuote} 
            author={author} setAuthor={setAuthor} 
            customizations={customizations} setCustomizations={setCustomizations} 
            currentStep={currentStep} setCurrentStep={setCurrentStep} 
            unlockedResolution={unlockedResolution} setUnlockedResolution={setUnlockedResolution} 
            autoDownloadTrigger={autoDownloadTrigger} setAutoDownloadTrigger={setAutoDownloadTrigger} 
            paymentStatus={paymentStatus} setPaymentStatus={setPaymentStatus} 
            posterRef={posterRef} resetApp={resetApp} 
          />} />
          <Route path="/about-us" element={<div className="pt-[56px]"><AboutUs /></div>} />
          <Route path="/contact-us" element={<div className="pt-[56px]"><ContactUs /></div>} />
          <Route path="/privacy-policy" element={<div className="pt-[56px]"><PrivacyPolicy /></div>} />
          <Route path="/blog" element={<div className="pt-[56px]"><BlogList /></div>} />
          <Route path="/blog/:id" element={<div className="pt-[56px]"><BlogPost /></div>} />
        </Routes>
        <RemoteFooter />
      </div>
    </Router>
  );
}

export default App;